import React from "react";
import { ButtonComp, CardComp, ModalCompAdvance } from "../../../../components";
import { useGetMemberLoanRepaymentReceiptQuery } from "../../../../store/member/member-loan";
import { AiOutlineClose } from "react-icons/ai";
import { formatMoney } from "../../../../utils";
import moment from "moment";
import { convertToTitleCase } from "../../../../utils/helpers";

const MemberLoanRepaymentReceiptModal = ({ repaymentId, setModalPage }) => {
  const {
    data: receiptData,
    isLoading,
    isError,
  } = useGetMemberLoanRepaymentReceiptQuery(repaymentId, {
    skip: !repaymentId,
  });

  const receipt = receiptData || {};

  if (isLoading) {
    return (
      <ModalCompAdvance
        show={true}
        handleClose={() => setModalPage("")}
        size="lg"
        centered
        close={true}
        modalBody={0}
        arrayComp={[
          {
            component: (
              <div className="p-4 text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading receipt...</p>
              </div>
            ),
          },
        ]}
      />
    );
  }

  if (isError || !receipt?.repayment) {
    return (
      <ModalCompAdvance
        show={true}
        handleClose={() => setModalPage("")}
        size="lg"
        centered
        close={true}
        modalTitle={
          <div className="d-flex justify-content-between align-items-center w-100">
            <h5 className="fw-bold mb-0">Payment Receipt</h5>
            <AiOutlineClose
              onClick={() => setModalPage("")}
              className="cursor-pointer"
              size={24}
              style={{ color: "var(--color-primary)" }}
            />
          </div>
        }
        modalBody={0}
        arrayComp={[
          {
            component: (
              <div className="text-center py-5">
                <p className="text-danger">Unable to load payment receipt.</p>
                <ButtonComp
                  btnText="Close"
                  btnClassName="bg-primary1 px-4 py-2 text-white rounded"
                  onClick={() => setModalPage("")}
                />
              </div>
            ),
          },
        ]}
      />
    );
  }

  const repayment = receipt.repayment || {};
  const transaction = receipt.transaction || {};
  const loanRequest = receipt.loan_request || {};
  const paymentMethod = receipt.payment_method || {};

  return (
    <ModalCompAdvance
      show={true}
      handleClose={() => setModalPage("")}
      size="lg"
      centered
      close={true}
      modalTitle={
        <div className="d-flex justify-content-between align-items-center w-100">
          <h4 className="fw-bold mb-0" style={{ color: "var(--color-primary)" }}>
            Payment Receipt
          </h4>
          <AiOutlineClose
            onClick={() => setModalPage("")}
            className="cursor-pointer"
            size={24}
            style={{ color: "var(--color-primary)" }}
          />
        </div>
      }
      modalBody={0}
      arrayComp={[
        {
            component: (
            <div className="p-4">
              <CardComp cardClassName="border shadow-sm">
          <div className="p-4">
            {/* Receipt Header */}
            <div className="text-center mb-4 pb-3 border-bottom">
              <h5 className="fw-bold mb-2" style={{ color: "#19bc31" }}>
                ✓ Payment Successful
              </h5>
              <p className="text-muted mb-0">
                Receipt Reference: {transaction?.reference || "N/A"}
              </p>
            </div>

            {/* Payment Details */}
            <div className="row mb-3">
              <div className="col-md-6 mb-3">
                <div>
                  <p className="text-muted small mb-1">Loan Type</p>
                  <p className="fw-semibold mb-0">
                    {loanRequest?.loan_type?.name || "N/A"}
                  </p>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div>
                  <p className="text-muted small mb-1">Payment Date</p>
                  <p className="fw-semibold mb-0">
                    {repayment?.paid_at
                      ? moment(repayment.paid_at).format("DD MMMM, YYYY [at] h:mm A")
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6 mb-3">
                <div>
                  <p className="text-muted small mb-1">Amount Paid</p>
                  <p className="fw-bold fs-4 mb-0" style={{ color: "#19bc31" }}>
                    {formatMoney(repayment?.amount || 0)}
                  </p>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div>
                  <p className="text-muted small mb-1">Payment Method</p>
                  <p className="fw-semibold mb-0">
                    {convertToTitleCase(paymentMethod?.name || "N/A")}
                  </p>
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6 mb-3">
                <div>
                  <p className="text-muted small mb-1">Repayment Due Date</p>
                  <p className="fw-semibold mb-0">
                    {repayment?.due_at
                      ? moment(repayment.due_at).format("DD MMMM, YYYY")
                      : "N/A"}
                  </p>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div>
                  <p className="text-muted small mb-1">Transaction Status</p>
                  <p className="mb-0">
                    <span
                      className="px-3 py-1 rounded-pill text-white"
                      style={{
                        backgroundColor: "#19bc31",
                        fontSize: "12px",
                      }}
                    >
                      {convertToTitleCase(transaction?.status || repayment?.status || "PAID")}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Loan Details */}
            <div className="border-top pt-3 mt-3">
              <h6 className="fw-bold mb-3">Loan Details</h6>
              <div className="row">
                <div className="col-md-6 mb-2">
                  <p className="text-muted small mb-1">Total Loan Amount</p>
                  <p className="fw-semibold mb-0">
                    {formatMoney(loanRequest?.amount || 0)}
                  </p>
                </div>
                <div className="col-md-6 mb-2">
                  <p className="text-muted small mb-1">Repayment Installment</p>
                  <p className="fw-semibold mb-0">
                    {repayment?.repayment_type || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-top pt-3 mt-3 text-center">
              <p className="text-muted small mb-0">
                This is an electronic receipt. No signature required.
              </p>
              <p className="text-muted small mb-0 mt-1">
                For inquiries, please contact support.
              </p>
            </div>
          </div>
        </CardComp>

        <div className="d-flex justify-content-end mt-4">
                <ButtonComp
                  btnText="Close"
                  btnClassName="bg-primary1 px-4 py-2 text-white rounded"
                  onClick={() => setModalPage("")}
                />
              </div>
            </div>
          ),
        },
      ]}
    />
  );
};

export default MemberLoanRepaymentReceiptModal;

